import { auth } from '@imobdesk/contracts'
import type {
  AbrirContaForm,
  LoginEmailPasswordForm,
  LoginMagicLinkForm,
  RecuperarSenhaForm,
  ValidateOtpForm,
} from './auth.schema'
import {
  abrirContaFormSchema,
  loginEmailPasswordFormSchema,
  loginMagicLinkFormSchema,
  mapAbrirContaFormToApiRequest,
  mapLoginEmailPasswordFormToApiRequest,
  mapLoginMagicLinkFormToApiRequest,
  mapRecuperarSenhaFormToApiRequest,
  mapValidateOtpFormToApiRequest,
  recuperarSenhaFormSchema,
  validateOtpFormSchema,
} from './auth.schema'
import type {
  AbrirContaResponse,
  LoginSuccessResponse,
  RecuperarSenhaResponse,
  RefreshTokenResponse,
} from './auth.types'
import { apiClientJsonParsed, parseWith } from '@/lib/api-client'
import { rethrowApiError } from '@/lib/api-error'
import {
  saveAccessToken,
  saveTokens,
} from '@/lib/token-storage'
import { API_BASE_URL } from '@/config/api'

/**
 * Action para login com email e senha
 */
export async function loginEmailPassword(
  data: LoginEmailPasswordForm,
): Promise<LoginSuccessResponse> {
  try {
    loginEmailPasswordFormSchema.parse(data)
    const requestData = mapLoginEmailPasswordFormToApiRequest(data)
    const response = await apiClientJsonParsed(
      '/api/v2/auth/login',
      auth.loginSuccessResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    // Salva os tokens no localStorage
    saveTokens(response.accessToken, response.refreshToken)

    return response
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao fazer login. Tente novamente.')
  }
}

/**
 * Resposta de sucesso do login magic link
 */
export interface MagicLinkResponse {
  success: boolean
  message: string
}

/**
 * Action para login com magic link
 */
export async function loginMagicLink(
  data: LoginMagicLinkForm,
): Promise<MagicLinkResponse> {
  try {
    loginMagicLinkFormSchema.parse(data)
    const requestData = mapLoginMagicLinkFormToApiRequest(data)
    const response = await apiClientJsonParsed(
      '/api/v2/auth/login-magic-link',
      auth.magicLinkResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    return response
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao enviar magic link. Tente novamente.')
  }
}

/**
 * Action para recuperar senha
 */
export async function recuperarSenha(
  data: RecuperarSenhaForm,
): Promise<RecuperarSenhaResponse> {
  try {
    recuperarSenhaFormSchema.parse(data)
    const requestData = mapRecuperarSenhaFormToApiRequest(data)
    const response = await apiClientJsonParsed(
      '/api/v2/auth/new-password',
      auth.recuperarSenhaResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    return response
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao enviar email de recuperação. Tente novamente.')
  }
}

/**
 * Action para abrir conta (cadastro)
 */
export async function abrirConta(
  data: AbrirContaForm,
): Promise<AbrirContaResponse> {
  try {
    abrirContaFormSchema.parse(data)
    const requestData = mapAbrirContaFormToApiRequest(data)
    const response = await apiClientJsonParsed(
      '/api/v2/auth/register',
      auth.abrirContaResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    return response
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao criar conta. Tente novamente.')
  }
}

/**
 * Action para validar magic link token (código OTP)
 */
export async function validateOtp(
  data: ValidateOtpForm,
): Promise<LoginSuccessResponse> {
  try {
    validateOtpFormSchema.parse(data)
    const requestData = mapValidateOtpFormToApiRequest(data)
    const response = await apiClientJsonParsed(
      '/api/v2/auth/check-magic-link',
      auth.loginSuccessResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    // Salva os tokens no localStorage
    saveTokens(response.accessToken, response.refreshToken)

    return response
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao validar código. Tente novamente.')
  }
}

/**
 * Action para validar magic link token apenas com o código (sem email)
 * Usado quando o usuário acessa pelo link direto
 */
export async function validateOtpByToken(
  token: string,
): Promise<LoginSuccessResponse> {
  try {
    const response = await apiClientJsonParsed(
      '/api/v2/auth/check-magic-link',
      auth.loginSuccessResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ token }),
      },
    )

    // Salva os tokens no localStorage
    saveTokens(response.accessToken, response.refreshToken)

    return response
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao validar código. Tente novamente.')
  }
}

/**
 * Resposta de sucesso do logout
 */
export interface LogoutResponse {
  success: boolean
  message: string
}

/**
 * Action para logout do usuário
 */
export async function logout(): Promise<LogoutResponse> {
  try {
    const response = await apiClientJsonParsed(
      '/api/v2/auth/logout',
      auth.logoutResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    )

    return response
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao fazer logout')
  }
}

/**
 * Action para renovar o access token usando o refresh token
 * Esta função faz uma chamada direta sem usar apiClientJson para evitar loop infinito
 */
export async function refreshToken(): Promise<RefreshTokenResponse> {
  try {
    // Faz chamada direta sem usar apiClient para evitar loop
    const url = API_BASE_URL
      ? `${API_BASE_URL}/api/v2/auth/refresh-token`
      : '/api/v2/auth/refresh-token'
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      credentials: 'include',
    })

    const contentType = response.headers.get('content-type')
    if (!contentType?.includes('application/json')) {
      const text = await response.text()
      throw new Error(`Resposta não é JSON: ${text}`)
    }

    const data = await response.json()

    if (!response.ok) {
      throw {
        status: response.status,
        statusText: response.statusText,
        data,
      }
    }

    // Valida a resposta no boundary com o contrato antes de confiar nela.
    const parsed = parseWith(auth.refreshTokenResponseSchema, data)
    saveAccessToken(parsed.accessToken)

    return parsed
  } catch (error: any) {
    rethrowApiError(error, 'Erro ao renovar token. Tente novamente.')
  }
}
